import type {RichTextEditorAction, RichTextEditorOptions} from 'grapesjs';
import {initMergeTagsDropdown, renderColorPickerDropdownForText, RTE_MERGE_TAGS_LIST_ID} from '@Global/email-maker';
import {unSelectedItemsForMultipleLists} from '@Components/list/list-reducer';
import {addTagToMap} from '@Components/merge-tags/merge-tags-reducer';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import {getMergeTagsFromOptionId} from '@/email-maker/grapesjs-email-editor-plugin/src/commands/editor';
import type {PrimaryConfigOptions} from '@/email-maker/grapesjs-email-editor-plugin/src/index';
import {updateColorForColorPicker, updateDoCloseDropdown} from '@Components/email-maker-color-picker-dropdown/email-maker-color-picker-dropdown-reducer';
import {colorToHexString} from '@Utils/color.util';

interface CustomRTE {
  settings: RichTextEditorOptions;
  classes: Record<string, string>;
  actionbar?: HTMLElement;
  actions: RichTextEditorAction[];
  el: HTMLElement;
  doc: Document;
  enabled?: boolean;
  getContent?: () => string;

  addAction(
    action: RichTextEditorAction,
    opts?: {
      sync?: boolean;
    }
  ): void;

  selection(): Selection | null;

  actionbarEl(): HTMLElement | undefined;
}

const DEFAULT_TEXT_COLOR = '#242424';
/**
 * Rich text editor
 */
export default (opt: PrimaryConfigOptions): void => {
  const {editor} = opt;
  const rte = editor.RichTextEditor;
  let lastSelectedTextConfig: Partial<Selection> | null = null;

  editor.on('rte:enable', () => {
    if (!window.PMW.emails.RTE_INIT) {
      removeRTEDefaults();
      addRTEActions();
      initHandlersForCustomURL();
    }

    updateColorPickerState();
    disableAndCloseCustomLinkBuilder();
    window.PMW.emails.RTE_INIT = true;
  });

  const updateColorPickerState = (): void => {
    const colorPickerActionCTA = rte.get('color')?.btn;

    if (!colorPickerActionCTA) {
      return;
    }

    const selection = rte.globalRte?.selection();
    lastSelectedTextConfig = getCopyOfSelectionObjectConfig(selection ?? null);
    const color = getColorFillForCurrentText(selection ?? null);

    window.PMW.redux.store.dispatch(updateColorForColorPicker(color));
    doCloseColorPickerDropDownIfNeeded(colorPickerActionCTA);
  };

  editor.on('rteToolbarPosUpdate', (pos) => {
    pos.top = '100%';
  });

  const initHandlersForCustomURL = (): void => {
    const doc = $(document);

    doc.on('keyup', '#custom-link-input', (e) => {
      if (e.key === 'Enter') {
        $('.rte-url-builder-container').trigger('change');
      }
    });

    doc.on('click', '.rte-custom-link', function (e) {
      const tar = $(e.target);
      const linkInput = $('#custom-link-input');
      const customLinkBtn = $(this) as JQuery;
      const elementToBeTriggered = $('.rte-url-builder-container');

      switch (tar.attr('data-type')) {
        case 'create':
        case 'close':
          toggleOpenState(customLinkBtn);

          break;
        case 'add':
          elementToBeTriggered.trigger('change');

          break;
        case 'delete':
          linkInput.val('');
          elementToBeTriggered.trigger('change');

          break;
      }
    });
  };

  const toggleOpenState = (el: JQuery): void => {
    const inputEl = el.find('.rte-email-input');

    el.toggleClass('-open');
    inputEl.toggleClass('_hidden');

    const parentContainer = inputEl.parents('.rte-url-builder-container');
    parentContainer.toggleClass('-open');

    const actionContainer = parentContainer.parents('.gjs-rte-action');
    actionContainer.siblings('.gjs-rte-action, .rte-action-divider').toggleClass('_hidden');

    if (!inputEl.hasClass('_hidden')) {
      inputEl.trigger('focus');
    }
  };

  const linkPlaceHolderText = window.i18next.t('pmwjs_link_placeholder');

  const addRTEActions = (): void => {
    rte.add('color', {
      icon: ((): HTMLElement => {
        const colorPickerContainer = document.createElement('div');
        colorPickerContainer.className = 'rte-color-picker-container radius-4';

        const initialSelection = rte.globalRte?.selection();
        const initialColor = getColorFillForCurrentText(initialSelection ?? null);

        renderColorPickerDropdownForText(colorPickerContainer, {
          initialColor: initialColor,
          onColorChange: (newColor: string): void => {
            rte.globalRte?.exec('foreColor', newColor);
            lastSelectedTextConfig = getCopyOfSelectionObjectConfig(rte.globalRte?.selection() ?? null);
          },
          onColorChangeEnd: (newColor: string): void => {
            rte.globalRte?.exec('foreColor', newColor);
            lastSelectedTextConfig = getCopyOfSelectionObjectConfig(rte.globalRte?.selection() ?? null);
          },
          onOpen: (): void => {
            setColorPickerCTAOpenAttribute(true);
          },
          onClose: (): void => {
            setColorPickerCTAOpenAttribute(false);
            rte.get('color')?.btn?.classList.remove('gjs-rte-active');
          },
          dropdownSelectorClasses: 'rte-color-picker-selector radius-4 border-s-dark',
          popupClassName: 'rte-color-picker-popup',
          popupSpacingClass: 'spacing-p-3',
          alignment: ALIGNMENT_TYPE.BOTTOM_START,
          topOffset: 8,
        });

        return colorPickerContainer;
      })(),

      attributes: {
        title: window.i18next.t('pmwjs_color'),
        class: 'gjs-rte-action',
      },

      // breaks code if removed
      result: (rteInstance, action) => {},

      update: (rteInstance, action) => {
        const colorPickerActionCTA = action.btn;
        const currentSelection = rteInstance.selection();

        if (!colorPickerActionCTA || !currentSelection) {
          return 0;
        }

        const isColorPickerOpen = colorPickerActionCTA.getAttribute('data-open') === 'true';
        const hasSelectionChanged = getHasSelectedObjectUpdated(lastSelectedTextConfig, currentSelection);
        const isCurrentSelectionRange = !currentSelection.isCollapsed;
        const isCurrentRangeSelectionLink = isCurrentSelectionRange && getIsCurrentSelectionLink(currentSelection);

        if (isColorPickerOpen) {
          action.btn?.classList.add('gjs-rte-active');
        } else {
          action.btn?.classList.remove('gjs-rte-active');
        }

        if (isCurrentRangeSelectionLink) {
          action.btn?.classList.add('_hidden');
          action.btn?.parentElement?.querySelector('.rte-action-divider')?.classList.add('_hidden');
        } else {
          action.btn?.classList.remove('_hidden');
          action.btn?.parentElement?.querySelector('.rte-action-divider')?.classList.remove('_hidden');
        }

        if (hasSelectionChanged) {
          const fillColor = getColorFillForCurrentText(currentSelection);
          window.PMW.redux.store.dispatch(updateColorForColorPicker(fillColor));
          doCloseColorPickerDropDownIfNeeded(colorPickerActionCTA);
        }

        lastSelectedTextConfig = getCopyOfSelectionObjectConfig(rteInstance.selection());
        return 0;
      },
    });

    rte.add('divider', {
      icon: '<div class=""></div>',
      attributes: {
        class: 'rte-action-divider spacing-m-l-1 bg-neutral-18 radius-round',
      },
      result: () => {
        return;
      },
    });

    rte.add('bold', {
      icon: '<i class="pmw-icon icon-bold rte-icon spacing-p-2"></i>',
      attributes: {title: window.i18next.t('pmwjs_bold')},
      result: (rteInstance) => {
        rteInstance.exec('bold');
      },
    });

    rte.add('italic', {
      icon: '<i class="pmw-icon icon-italic rte-icon spacing-p-2"></i>',
      attributes: {title: window.i18next.t('pmwjs_italic')},
      result: (rteInstance) => {
        rteInstance.exec('italic');
      },
    });

    rte.add('underline', {
      icon: '<i class="pmw-icon icon-underline rte-icon spacing-p-2"></i>',
      attributes: {title: window.i18next.t('pmwjs_underline')},
      result: (rteInstance) => {
        rteInstance.exec('underline');
      },
    });

    rte.add('strikethrough', {
      icon: '<i class="pmw-icon icon-strike-through rte-icon spacing-p-2"></i>',
      attributes: {title: window.i18next.t('pmwjs_strikethrough')},
      result: (rteInstance) => {
        rteInstance.exec('strikethrough');
      },
    });

    rte.add('custom-link', {
      icon:
        '<div class="rte-url-builder-container" data-type="create">' +
        '<div class="icon-container link flex-center radius-4 _full-height"><i class="pmw-icon size-icon-16 icon-link rte-icon spacing-p-2" data-type="create"></i></div>' +
        `<div class="_hidden rte-email-input"><input name="link" placeholder="${linkPlaceHolderText}" id="custom-link-input" value="" />` +
        '<div class="icon-container flex-center spacing-p-1 _full-height close-input"><i data-type="close" class="pmw-icon icon-close spacing-p-2"></i></div>' +
        '<div class="icon-container flex-center spacing-p-1 _full-height delete-input -danger" data-type="delete"><i data-type="delete" class="pmw-icon icon-delete spacing-p-2"></i></div>' +
        '<div class="icon-container flex-center spacing-p-1 _full-height add-input"><i data-type="add" class="pmw-icon icon-check spacing-p-2"></i></div>' +
        '</div>' +
        '</div>',
      attributes: {
        title: window.i18next.t('pmwjs_link'),
        class: 'gjs-rte-action rte-custom-link',
        'data-type': 'create',
      },
      event: 'change',
      result: (rteInstance) => {
        const val = $('#custom-link-input').val();

        if (val) {
          const validatedURL = editor.runCommand(window.PMW.emails.commands.GET_VALIDATED_URL, {
            url: val,
          }) as string;

          rteInstance.exec('createLink', validatedURL);

          editor.runCommand(window.PMW.emails.commands.SET_COMPONENT_STYLE, {
            component: editor.getSelected(),
            property: window.PMW.emails.styles.LINK_COLOR,
            value: '#0000EE',
          });

          editor.runCommand(window.PMW.emails.commands.REFRESH_STYLEMANAGER);
        } else {
          if (rteInstance.selection()?.focusNode?.parentElement) {
            rteInstance.selection()?.focusNode?.parentElement?.removeAttribute('style');
          }

          rteInstance.exec('unlink');
        }

        $('.rte-custom-link').click();
      },
      update: (rteInstance, action) => {
        const url = getLinkOptionURL(rteInstance);
        const actionSelectorParent = $('.rte-url-builder-container');
        const selectedNode = rteInstance.selection()?.focusNode ? rteInstance.selection()?.focusNode : editor.getSelected()?.getEl();

        const customLinkEl: JQuery = $('.rte-custom-link');

        if (editor.getSelected()?.getEl()?.getAttribute('data-pmw-type') === 'postermywall-button') {
          customLinkEl.addClass('-disabled');
          return 0;
        }

        if (rteInstance.selection()?.anchorOffset === rteInstance.selection()?.focusOffset) {
          disableAndCloseCustomLinkBuilder();
          return 0;
        }

        customLinkEl.removeClass('-disabled');

        // eslint-disable-next-line
        // @ts-ignore
        const actionBtn: JQuery = $(action.btn);

        if (selectedNode) {
          if (url) {
            actionSelectorParent.find('.delete-input').removeClass('_hidden');
            actionSelectorParent.find('.close-input').addClass('_hidden');
            actionBtn.addClass('gjs-rte-active');
          } else {
            actionSelectorParent.find('.delete-input').addClass('_hidden');
            actionSelectorParent.find('.close-input').removeClass('_hidden');
            actionBtn.removeClass('gjs-rte-active');
          }
        }

        const customLinkInput = document.getElementById('custom-link-input');

        if (customLinkInput) {
          // eslint-disable-next-line
          // @ts-ignore
          customLinkInput.value = url || null;
        }

        return 0;
      },
    });

    let selectedItem: string;
    rte.add('merge-tags', {
      icon: ((): HTMLElement => {
        const dropdown = document.createElement('div');
        dropdown.setAttribute('id', RTE_MERGE_TAGS_LIST_ID);
        initMergeTagsDropdown(dropdown, {
          listId: RTE_MERGE_TAGS_LIST_ID,
          onSelect: (item) => {
            selectedItem = item;
            rte.globalRte?.insertHTML(getMergeTagsFromOptionId(selectedItem), {select: true});
          },
          alignment: ALIGNMENT_TYPE.BOTTOM_START,
        });
        return dropdown;
      })(),
      attributes: {title: window.i18next.t('pmwjs_add_merge_tag')},
      result: () => {
        const selectedComponent = editor.getSelected();

        if (!selectedItem) {
          return;
        }

        if (!selectedComponent) {
          return;
        }

        window.PMW.redux.store.dispatch(addTagToMap({ccid: selectedComponent.ccid, mergeTagKey: selectedItem}));
        window.PMW.redux.store.dispatch(unSelectedItemsForMultipleLists({listIds: [RTE_MERGE_TAGS_LIST_ID]}));
      },
    });
  };

  const disableAndCloseCustomLinkBuilder = (): void => {
    const customLinkCTA = rte.get('custom-link')?.btn;
    customLinkCTA?.classList.add('-disabled');

    const isOpen = customLinkCTA?.classList.contains('-open');
    if (!isOpen) {
      return;
    }

    const customLinkBuilderContainer = customLinkCTA?.querySelector('.rte-url-builder-container');
    const inputEl = customLinkCTA?.querySelector('.rte-email-input');
    const allActions = rte.getAll();

    customLinkBuilderContainer?.classList.remove('-open');
    customLinkCTA?.classList.remove('-open');
    allActions.forEach((action) => {
      action.btn?.classList.remove('_hidden');
    });

    inputEl?.classList.add('_hidden');
  };

  const doCloseColorPickerDropDownIfNeeded = (colorPickerCTA: HTMLElement): void => {
    const isOpenAttr = colorPickerCTA.getAttribute('data-open');
    if (!isOpenAttr) {
      return;
    }

    const isColorPickerOpen = getBooleanFromString(isOpenAttr);
    if (!isColorPickerOpen) {
      return;
    }

    window.PMW.redux.store.dispatch(updateDoCloseDropdown(true));
  };

  function getBooleanFromString(value: string): boolean {
    return value?.toLowerCase() === 'true';
  }

  const getCopyOfSelectionObjectConfig = (selection: Selection | null): Partial<Selection> | null => {
    if (!selection) {
      return null;
    }

    return {
      anchorNode: selection.anchorNode,
      focusNode: selection.focusNode,
      anchorOffset: selection.anchorOffset,
      focusOffset: selection.focusOffset,
      isCollapsed: selection.isCollapsed,
    };
  };

  const setColorPickerCTAOpenAttribute = (isOpen: boolean): void => {
    const colorPickerActionCta = rte.get('color')?.btn;
    colorPickerActionCta?.setAttribute('data-open', isOpen.toString());
  };

  const getHasSelectedObjectUpdated = (lastSelection: Partial<Selection> | null, currentSelection: Selection): boolean => {
    if (!lastSelection) {
      return false;
    }

    return (
      lastSelection.anchorNode !== currentSelection.anchorNode ||
      lastSelection.focusNode !== currentSelection.focusNode ||
      lastSelection.anchorOffset !== currentSelection.anchorOffset ||
      lastSelection.focusOffset !== currentSelection.focusOffset
    );
  };

  const getIsCurrentSelectionLink = (selection: Selection): boolean => {
    if (selection.rangeCount === 0) {
      return false;
    }

    const node = selection.anchorNode;

    if (node?.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).tagName === 'A') {
      return true;
    }

    return node?.nodeType === Node.TEXT_NODE && node.parentElement?.tagName === 'A';
  };

  const getColorFillForCurrentText = (selection: Selection | null): string => {
    if (!selection) {
      return DEFAULT_TEXT_COLOR;
    }

    if (selection.rangeCount === 0) {
      return DEFAULT_TEXT_COLOR;
    }

    const range = selection.getRangeAt(0);
    const selectedNode =
      range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE ? (range.commonAncestorContainer as HTMLElement) : range.commonAncestorContainer.parentElement;

    if (!selectedNode) {
      return DEFAULT_TEXT_COLOR;
    }

    return colorToHexString(window.getComputedStyle(selectedNode).color);
  };

  const getLinkOptionURL = (rteInstance: CustomRTE): string => {
    const selectedNode = (rteInstance.selection()?.focusNode ? rteInstance.selection()?.focusNode : editor.getSelected()?.getEl()) as HTMLElement;
    let url = '';

    if (selectedNode) {
      if (typeof selectedNode.getAttribute === 'function') {
        url = selectedNode.getAttribute('href')!;
      } else if (typeof selectedNode.parentElement !== 'undefined') {
        url = selectedNode.parentElement?.getAttribute('href')!;
      }
    }

    return url;
  };

  const removeRTEDefaults = (): void => {
    rte.remove('bold');
    rte.remove('italic');
    rte.remove('underline');
    rte.remove('strikethrough');
    rte.remove('link');
    rte.remove('orderedlist');
    rte.remove('unorderedlist');
  };
};
